import type { DocumentItem } from "@/types/ui-models";
import { NoRealData } from "@/components/shared";
import DocumentStatusBadge from "./DocumentStatusBadge";
import { dash, fmtDate } from "../lib/docUtils";

interface EnvironmentalSourcesTableProps {
  documents: DocumentItem[];
  onViewDocument: (id: string) => void;
}

const bindingTypeLabel: Record<string, string> = {
  area: "Área ambiental",
  uc: "Unidade de Conservação",
  compensation: "Compensação",
};

export default function EnvironmentalSourcesTable({ documents, onViewDocument }: EnvironmentalSourcesTableProps) {
  const areaDocs = documents.filter((d) => d.primaryBindingType in bindingTypeLabel);

  if (areaDocs.length === 0) {
    return (
      <NoRealData
        title="Nenhum documento vinculado a área ambiental ou UC"
        description="Documentos cadastrados com vínculo a áreas verdes, Unidades de Conservação ou compensações aparecem aqui."
        icon="ri-leaf-line"
      />
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground-900">Documentos vinculados a áreas ambientais e UCs</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200 text-left">
              <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Área/Unidade</th>
              <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Tipo de vínculo</th>
              <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Documento</th>
              <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Órgão emissor</th>
              <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Emissão</th>
              <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Validade</th>
              <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Status</th>
              <th className="py-3 px-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {areaDocs.map((doc) => (
              <tr key={doc.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                <td className="py-2.5 px-3">
                  <span className="text-sm font-semibold text-foreground-800">{dash(doc.areaName || doc.primaryBinding)}</span>
                </td>
                <td className="py-2.5 px-3">
                  <span className="text-xs text-foreground-500">{bindingTypeLabel[doc.primaryBindingType]}</span>
                </td>
                <td className="py-2.5 px-3">
                  <button onClick={() => onViewDocument(doc.id)} className="text-sm font-medium text-foreground-800 hover:text-accent-600 transition-colors text-left max-w-[200px] truncate block">
                    {doc.title}
                  </button>
                </td>
                <td className="py-2.5 px-3 text-xs text-foreground-500 max-w-[150px] truncate">{dash(doc.issuingBody)}</td>
                <td className="py-2.5 px-3 text-xs text-foreground-500 whitespace-nowrap">{fmtDate(doc.issueDate)}</td>
                <td className="py-2.5 px-3 text-xs text-foreground-500 whitespace-nowrap">{fmtDate(doc.expiryDate)}</td>
                <td className="py-2.5 px-3"><DocumentStatusBadge status={doc.status} /></td>
                <td className="py-2.5 px-3">
                  <button onClick={() => onViewDocument(doc.id)} className="w-7 h-7 rounded-md hover:bg-background-100 flex items-center justify-center text-foreground-500 hover:text-accent-600 transition-colors" title="Visualizar">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
